import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, exportToExcel, Typography, useRouteDocumentTitle, Pagination } from '@client/toolkit';
import { FaCalendarAlt } from 'react-icons/fa';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { Table } from './components';
import { NetYieldCard } from '../components';
import { DateRangeFilter, futureDate, notWithinYearRange } from '../components/date-range-filter';
import { dateRangeOptions } from '../utils/date-range-options';
import {
  useGetStatisticsNetYieldQuery,
  useGetStatisticsNetYieldBuildingsQuery,
  useLazyGetStatisticsNetYieldBuildingsExportQuery,
} from '@client/api';
import dayjs from 'dayjs';
import { transformToExcelFormat } from './utils/transformToExcelFormat';

export function NetYieldRoute() {
  const { t, i18n } = useTranslation('reporting');
  const defaultDateRange = {
    startDate: dateRangeOptions().lastTwelveMonths.startDate,
    endDate: dateRangeOptions().lastTwelveMonths.endDate,
  };
  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: defaultDateRange.startDate,
    endDate: defaultDateRange.endDate,
  });
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.Invoices);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const startDate = dayjs(timeRange.startDate).format('YYYY-MM-DD');
  const endDate = dayjs(timeRange.endDate).format('YYYY-MM-DD');

  const [triggerExport] = useLazyGetStatisticsNetYieldBuildingsExportQuery();

  const onExportExcel = useCallback(async () => {
    setExporting(true);
    const result = await triggerExport({ startDate, endDate });
    if (result.isSuccess) {
      exportToExcel(transformToExcelFormat(result.data, selectedRows, endDate), 'net-yield-buildings.xlsx');
    }
    setExporting(false);
  }, [triggerExport, startDate, endDate, selectedRows]);

  const { data: summary, isLoading: isLoadingSummary, isFetching: isFetchingSummary } = useGetStatisticsNetYieldQuery({ startDate, endDate });
  const {
    data: buildingsData,
    isFetching: isFetchingDetails,
    isLoading: isLoadingDetails,
  } = useGetStatisticsNetYieldBuildingsQuery({ startDate, endDate });

  const dateLabel =
    timeRange.startDate.toDateString() === defaultDateRange.startDate.toDateString() &&
    timeRange.endDate.toDateString() === defaultDateRange.endDate.toDateString()
      ? dateRangeOptions().lastTwelveMonths.displayLabel
      : t('dateRange', {
          startDate: timeRange.startDate.toLocaleDateString(i18n.language),
          endDate: timeRange.endDate.toLocaleDateString(i18n.language),
        });

  const getPeriodLabel = (startDate: Date, endDate: Date) => {
    if (dayjs(startDate).isSame(dayjs(endDate), 'year')) return dayjs(startDate).year().toString();
    return `${dayjs(startDate).year().toString()} - ${dayjs(endDate).year().toString()}`;
  };

  return (
    <ReportPageContent
      breadcrumb={t('navigation.reporting.netYield')}
      menu={
        <DateRangeFilter
          options={[
            dateRangeOptions().lastTwelveMonths,
            dateRangeOptions().currentVsPreviousYear,
            dateRangeOptions().custom,
          ]}
          onChange={(dateRange) => {
            setTimeRange({ startDate: dateRange[0], endDate: dateRange[1] });
            setCurrentPage(0);
            setSelectedRows(new Set());
          }}
          shouldDisableDate={notWithinYearRange}
          onExportExcel={onExportExcel}
          exporting={exporting}
          disabled={isLoadingSummary || isFetchingSummary || isLoadingDetails || isFetchingDetails}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <NetYieldCard
          loading={isLoadingSummary || isFetchingSummary}
          title={t('netYield.avgYieldYear', { year: getPeriodLabel(timeRange.startDate, timeRange.endDate) })}
          value={summary?.selectedNetYield ?? 0}
        />
        <NetYieldCard
          loading={isLoadingSummary || isFetchingSummary}
          title={t('netYield.avgYieldYear', {
            year: getPeriodLabel(
              dayjs(timeRange.startDate).subtract(1, 'year').toDate(),
              dayjs(timeRange.endDate).subtract(1, 'year').toDate(),
            ),
          })}
          value={summary?.previousYearNetYield ?? 0}
        />
        <NetYieldCard
          loading={isLoadingSummary || isFetchingSummary}
          title={t('development')}
          value={summary?.developmentVariance ?? 0}
          showDirection
        />
      </div>
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">{dateLabel}</Typography>
        </span>
        <Card className="pb-4">
          <Table
            data={buildingsData?.items ?? []}
            loading={isFetchingDetails || isLoadingDetails}
            onSelected={(ids) => {
              setSelectedRows(ids);
            }}
            startDate={startDate}
            endDate={endDate}
          />
          <Pagination
            totalCount={buildingsData?.total ?? 0}
            currentPage={currentPage}
            setPage={setCurrentPage}
            pageSize={buildingsData?.pageSize ?? 10}
            visible
          />
        </Card>
      </div>
    </ReportPageContent>
  );
}
